import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';

class ClogCartOffers extends HTMLElement {
  static get observedAttributes() {
    return ['data-lead', 'data-mid'];
  }

  connectedCallback() {
    this.#syncFill(true);
    this.#bindCta();
  }

  attributeChangedCallback() {
    this.#syncFill(false);
  }

  #bindCta() {
    if (this.dataset.ctaBound === 'true') return;
    this.dataset.ctaBound = 'true';

    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('button[data-clog-offer-code]') : null;
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;

      event.preventDefault();
      applyOfferCode(button.getAttribute('data-clog-offer-code'), button, this);
    });
  }

  /**
   * @param {boolean} animateFromZero
   */
  #syncFill(animateFromZero) {
    const lead = Math.max(0, Math.min(Number(this.dataset.lead || 0), 100));
    const mid = Math.max(0, Math.min(Number(this.dataset.mid || 0), 100));
    const fills = { lead, mid };

    this.querySelectorAll('[data-clog-fill]').forEach((fill) => {
      if (!(fill instanceof HTMLElement)) return;
      const progress = fills[fill.dataset.clogFill] ?? 0;

      if (animateFromZero && !this.hasAttribute('data-filled')) {
        fill.style.setProperty('--clog-cart-bar-progress', '0%');
        requestAnimationFrame(() => {
          fill.style.setProperty('--clog-cart-bar-progress', `${progress}%`);
        });
      } else {
        fill.style.setProperty('--clog-cart-bar-progress', `${progress}%`);
      }
    });

    this.setAttribute('data-filled', '');
    this.classList.add('is-ready');
  }

  /**
   * @param {string} message
   */
  showError(message) {
    const error = this.querySelector('.clog-cart-bar__error');
    if (!(error instanceof HTMLElement)) return;
    error.hidden = !message;
    error.textContent = message;
  }
}

if (!customElements.get('clog-cart-offers')) {
  customElements.define('clog-cart-offers', ClogCartOffers);
}

function cartUpdateUrl() {
  const fromTheme =
    (typeof globalThis.Theme === 'object' && globalThis.Theme?.routes?.cart_update_url) ||
    document.querySelector('[data-cart-update-url]')?.getAttribute('data-cart-update-url');

  return fromTheme || '/cart/update.js';
}

/**
 * @param {string | undefined} sectionId
 * @returns {string[]}
 */
function existingDiscountCodes(sectionId) {
  const root = sectionId
    ? document.querySelector(`cart-discount-component[data-section-id="${CSS.escape(sectionId)}"]`)
    : document.querySelector('cart-discount-component');

  return Array.from(root?.querySelectorAll('.cart-discount__pill') || [])
    .map((pill) => (pill instanceof HTMLElement ? pill.dataset.discountCode : ''))
    .filter((code) => Boolean(code));
}

/**
 * @param {Element} el
 * @returns {string | undefined}
 */
function sectionIdFrom(el) {
  return (
    el.closest('clog-cart-offers')?.dataset.sectionId ||
    el.closest('cart-discount-component')?.dataset.sectionId ||
    el.closest('.cart-page')?.querySelector('cart-discount-component')?.dataset.sectionId ||
    el.closest('cart-drawer-component')?.querySelector('cart-discount-component')?.dataset.sectionId ||
    document.querySelector('cart-discount-component')?.dataset.sectionId
  );
}

/**
 * @param {string[]} codes
 * @param {string} sectionId
 * @param {string} [attemptedCode]
 * @returns {Promise<boolean>}
 */
async function updateCartDiscounts(codes, sectionId, attemptedCode) {
  const response = await fetch(
    cartUpdateUrl(),
    fetchConfig('json', {
      body: JSON.stringify({
        discount: codes.join(','),
        sections: sectionId ? [sectionId] : [],
      }),
    })
  );

  const data = await response.json();

  if (attemptedCode && Array.isArray(data.discount_codes)) {
    const applied = data.discount_codes.find(
      (item) => item.code?.toLowerCase() === attemptedCode.toLowerCase()
    );
    if (applied && applied.applicable === false) {
      return false;
    }
  }

  const html = sectionId ? data.sections?.[sectionId] : null;
  if (html) {
    morphSection(sectionId, html, {
      mode: document.querySelector(`#shopify-section-${sectionId} theme-drawer`) ? 'hydration' : 'full',
    });
    return true;
  }

  window.location.reload();
  return true;
}

/**
 * @param {string | null} code
 * @param {HTMLButtonElement} button
 * @param {ClogCartOffers} host
 */
async function applyOfferCode(code, button, host) {
  if (!code) return;

  const sectionId = sectionIdFrom(host) || sectionIdFrom(button);
  const codes = existingDiscountCodes(sectionId);
  if (codes.some((item) => item.toLowerCase() === code.toLowerCase())) return;

  button.classList.add('is-loading');
  host.showError('');

  try {
    const ok = await updateCartDiscounts([...codes, code], sectionId || '', code);
    if (!ok) {
      host.showError('This discount cannot be applied to your cart.');
      button.classList.remove('is-loading');
    }
  } catch (error) {
    host.showError('Could not apply discount. Try again.');
    button.classList.remove('is-loading');
  }
}

function bindDiscountBridge() {
  if (window.__clogCartDiscountBridge) return;
  window.__clogCartDiscountBridge = true;

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.classList.contains('cart-discount__form')) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const input = form.querySelector('input[name="discount"]');
      const code = input instanceof HTMLInputElement ? input.value.trim() : '';
      const sectionId = sectionIdFrom(form);
      if (!code) return;

      const codes = existingDiscountCodes(sectionId);
      if (codes.some((item) => item.toLowerCase() === code.toLowerCase())) {
        input.value = '';
        return;
      }

      input.value = '';
      updateCartDiscounts([...codes, code], sectionId || '', code);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const remove = target.closest('.cart-discount__pill-remove');
      if (!(remove instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const pill = remove.closest('.cart-discount__pill');
      const code = pill instanceof HTMLElement ? pill.dataset.discountCode : '';
      const sectionId = sectionIdFrom(remove);
      if (!code) return;
      updateCartDiscounts(
        existingDiscountCodes(sectionId).filter((item) => item !== code),
        sectionId || ''
      );
    },
    true
  );
}

bindDiscountBridge();
